import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle, CheckCircle2, Info, Trash2 } from "lucide-react";
import { Modal } from "components/ui/modal";
import { Button } from "components/ui/button";
import { Input } from "components/ui/input";
import { useI18n } from "i18n";

/**
 * Substitui window.alert / window.confirm / window.prompt.
 *
 * Uso:
 *   const { confirm, alert } = useDialog();
 *   if (await confirm({ title: "Excluir?", variant: "danger" })) { ... }
 *   await alert({ title: "Salvo", variant: "success" });
 *
 * Acao destrutiva em massa: `requireText` exige que o usuario DIGITE a palavra
 * antes de o botao de confirmar habilitar (sem diferenciar maiusculas):
 *   await confirm({ title: "Limpar fotos?", variant: "danger",
 *                   requireText: "excluir", onConfirm: ... });
 */
const DialogContext = createContext(null);

const variantIcons = {
  danger: {
    Icon: Trash2,
    wrapper: "bg-red-100 dark:bg-red-900/30",
    icon: "text-red-600 dark:text-red-400",
  },
  warning: {
    Icon: AlertTriangle,
    wrapper: "bg-amber-100 dark:bg-amber-900/30",
    icon: "text-amber-600 dark:text-amber-400",
  },
  success: {
    Icon: CheckCircle2,
    wrapper: "bg-emerald-100 dark:bg-emerald-900/30",
    icon: "text-emerald-600 dark:text-emerald-400",
  },
  info: {
    Icon: Info,
    wrapper: "bg-blue-100 dark:bg-blue-900/30",
    icon: "text-blue-600 dark:text-blue-400",
  },
};

function DialogIcon({ variant }) {
  const config = variantIcons[variant] || variantIcons.info;
  const { Icon } = config;
  return (
    <div
      className={`w-12 h-12 rounded-full flex items-center justify-center ${config.wrapper}`}
    >
      <Icon className={`h-5 w-5 ${config.icon}`} />
    </div>
  );
}

export function DialogProvider({ children }) {
  const { t, tf } = useI18n();
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");
  const resolverRef = useRef(null);

  const close = useCallback((result) => {
    setDialog(null);
    setBusy(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  }, []);

  const open = useCallback((config) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setTyped("");
      setDialog(config);
    });
  }, []);

  const confirm = useCallback(
    (options = {}) =>
      open({
        type: "confirm",
        variant: "warning",
        confirmLabel: t("common.confirm"),
        cancelLabel: t("common.cancel"),
        ...options,
      }),
    [open, t]
  );

  const alert = useCallback(
    (options = {}) =>
      open({
        type: "alert",
        variant: "info",
        confirmLabel: "OK",
        ...options,
      }),
    [open]
  );

  const normalize = (value) => (value || "").trim().toLocaleLowerCase();
  const textMatches =
    !dialog?.requireText || normalize(typed) === normalize(dialog.requireText);

  const handleConfirm = async () => {
    if (!textMatches) return;
    // onConfirm opcional: mantem o modal aberto enquanto a acao executa.
    if (dialog?.onConfirm) {
      setBusy(true);
      try {
        await dialog.onConfirm();
      } catch (err) {
        setBusy(false);
        return;
      }
    }
    close(true);
  };

  const isConfirm = dialog?.type === "confirm";

  return (
    <DialogContext.Provider value={{ confirm, alert }}>
      {children}
      <Modal
        open={!!dialog}
        onClose={busy ? undefined : () => close(false)}
        title={dialog?.title}
        description={dialog?.description}
        icon={<DialogIcon variant={dialog?.variant} />}
        size={dialog?.size || "sm"}
        children={
          dialog?.requireText ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
              className="space-y-2 text-left"
            >
              <label htmlFor="dialog-require-text" className="block text-muted-foreground">
                {dialog.requireTextLabel ||
                  tf("dialog.typeToConfirm", { text: dialog.requireText })}
              </label>
              <Input
                id="dialog-require-text"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                disabled={busy}
                autoComplete="off"
                autoFocus
                placeholder={dialog.requireText}
              />
            </form>
          ) : null
        }
        footer={
          <>
            {isConfirm && (
              <Button
                variant="outline"
                onClick={() => close(false)}
                disabled={busy}
              >
                {dialog?.cancelLabel}
              </Button>
            )}
            <Button
              variant={dialog?.variant === "danger" ? "destructive" : "default"}
              onClick={handleConfirm}
              loading={busy}
              disabled={!textMatches}
              autoFocus={!dialog?.requireText}
            >
              {dialog?.confirmLabel}
            </Button>
          </>
        }
      />
    </DialogContext.Provider>
  );
}

export function useDialog() {
  const ctx = useContext(DialogContext);
  if (!ctx) {
    throw new Error("useDialog deve ser usado dentro de <DialogProvider>");
  }
  return ctx;
}
